import { FirebaseError } from 'firebase/app';

interface ZodIssue {
  path: (string | number)[];
  message: string;
  code?: string;
}

/**
 * Parses a Firebase/Zod error and returns a user-friendly message
 */
export function parseFirebaseError(error: unknown): string {
  // Handle FirebaseError with Zod validation details
  if (error instanceof FirebaseError) {
    // Check if it's a validation error with Zod issues in details
    const details = (error as FirebaseError & { details?: unknown }).details;

    if (Array.isArray(details) && details.length > 0) {
      return formatZodIssues(details as ZodIssue[]);
    }

    // Return the Firebase error message
    return error.message || 'An error occurred';
  }

  // Handle standard Error
  if (error instanceof Error) {
    return error.message;
  }

  // Fallback for unknown error types
  return 'An unexpected error occurred';
}

/**
 * Formats Zod validation issues into a readable message
 */
export function formatZodIssues(issues: ZodIssue[]): string {
  if (issues.length === 0) {
    return 'Validation failed';
  }

  if (issues.length === 1) {
    const issue = issues[0];
    const path = formatPath(issue.path);
    return path ? `${path}: ${issue.message}` : issue.message;
  }

  // Multiple issues - list them
  const formattedIssues = issues.slice(0, 3).map((issue) => {
    const path = formatPath(issue.path);
    return path ? `${path}: ${issue.message}` : issue.message;
  });

  const remaining = issues.length - 3;
  if (remaining > 0) {
    formattedIssues.push(
      `...and ${remaining} more issue${remaining > 1 ? 's' : ''}`,
    );
  }

  return formattedIssues.join('; ');
}

/**
 * Formats a Zod path array into a readable string
 */
function formatPath(path: (string | number)[]): string {
  if (!path || path.length === 0) {
    return '';
  }

  return path
    .map((segment, index) => {
      if (typeof segment === 'number') {
        return `[${segment}]`;
      }
      // Convert camelCase to Title Case
      const formatted = segment
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
      return index === 0 ? formatted : formatted.toLowerCase();
    })
    .join(' → ');
}
